import { EventEmitter, Inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SplitFactory } from '@splitsoftware/splitio-browserjs';
import { environment } from '@env/environment';

type SplitFactoryFn = (settings: SplitIO.IBrowserSettings) => SplitIO.ISDK;

export const SPLIT_FACTORY = new InjectionToken<SplitFactoryFn>('SplitFactory', {
    providedIn: 'root',
    factory: () => SplitFactory
});

@Injectable({
    providedIn: 'root'
})
export class SplitService {

    private static readonly LOCAL_OVERRIDE_PREFIX = 'mmp.split.';
    private static readonly LOCAL_OVERRIDE_TREATMENTS = ['on', 'off', 'control'];
    private static readonly DEFAULT_TREATMENT: SplitIO.Treatment = 'off';
    private static readonly READY_TIMEOUT_SECONDS = 3;

    authorizationKey = environment.splitAuthorizationKey;
    key: string = 'default';
    public treatmentUpdated: EventEmitter<string> = new EventEmitter();

    private client: SplitIO.IClient;
    private ready$ = new BehaviorSubject<boolean>(false);
    private initialization: Promise<void> | null = null;
    private treatmentCache = new Map<string, BehaviorSubject<SplitIO.Treatment>>();
    private splitUnavailable = false;

    constructor(
        private ngZone: NgZone,
        @Inject(SPLIT_FACTORY) private splitFactory: SplitFactoryFn
    ) {
        // The client is created by the APP_INITIALIZER (initializeSplit), not here.
        // Creating one in the constructor as well left the first client running and
        // unreferenced for the life of the tab.
    }

    initializeSplitClient(key: string) {
        this.key = key;
        this.authorizationKey = environment.splitAuthorizationKey;
        this.ready$.next(false);
        this.splitUnavailable = false;

        const sdk: SplitIO.ISDK = this.splitFactory({
            core: {
                authorizationKey: this.authorizationKey,
                key: key,
            },
            startup: {
                readyTimeout: SplitService.READY_TIMEOUT_SECONDS
            }
        });

        this.client = sdk.client();

        this.client.on(this.client.Event.SDK_READY, () => {
            this.ngZone.run(() => {
                this.splitUnavailable = false;
                this.ready$.next(true);
                this.treatmentCache.forEach((subject, feature) => {
                    subject.next(this.resolveTreatment(feature));
                });
            });
        });

        this.client.on(this.client.Event.SDK_READY_TIMED_OUT, () => {
            this.ngZone.run(() => {
                this.useFallbackTreatments();
            });
        });

        this.client.on(this.client.Event.SDK_UPDATE, () => {
            this.ngZone.run(() => {
                this.treatmentUpdated.emit();
                this.treatmentCache.forEach((subject, feature) => {
                    subject.next(this.resolveTreatment(feature));
                });
            });
        });
    }

    getTreatmentObservable(feature: string): Observable<SplitIO.Treatment> {
        if (!this.treatmentCache.has(feature)) {
            this.treatmentCache.set(feature, new BehaviorSubject<SplitIO.Treatment>('control'));
        }

        const subject = this.treatmentCache.get(feature)!;
        const override = this.getLocalTreatmentOverride(feature);

        if (override) {
            subject.next(override);
        } else if (this.splitUnavailable) {
            subject.next(SplitService.DEFAULT_TREATMENT);
        } else if (this.ready$.getValue() && this.client) {
            subject.next(this.client.getTreatment(feature));
        }

        return subject.asObservable().pipe(filter((t) => t !== 'control'));
    }

    getTreatment(feature: string): Promise<SplitIO.Treatment> {
        const override = this.getLocalTreatmentOverride(feature);

        if (override) {
            return Promise.resolve(override);
        }

        if (this.splitUnavailable || !this.client) {
            return Promise.resolve(SplitService.DEFAULT_TREATMENT);
        }

        return new Promise((resolve) => {
            this.client.ready().then(() => {
                try {
                    resolve(this.client.getTreatment(feature));
                } catch (error) {
                    this.useFallbackTreatments();
                    resolve(SplitService.DEFAULT_TREATMENT);
                }
            }).catch(() => {
                this.useFallbackTreatments();
                resolve(SplitService.DEFAULT_TREATMENT);
            });
        });
    }

    initialize(key: string = 'default'): Promise<void> {
        this.initializeSplitClient(key);

        this.initialization = this.client.ready()
            .then(() => undefined)
            .catch(() => {
                this.useFallbackTreatments();
            });

        return this.initialization;
    }

    private resolveTreatment(feature: string): SplitIO.Treatment {
        const override = this.getLocalTreatmentOverride(feature);

        if (override) {
            return override;
        }

        return this.client
            ? this.client.getTreatment(feature)
            : SplitService.DEFAULT_TREATMENT;
    }

    private getLocalTreatmentOverride(feature: string): SplitIO.Treatment | null {
        if (!this.isLocalOverrideAllowed()) {
            return null;
        }

        const value = localStorage.getItem(`${SplitService.LOCAL_OVERRIDE_PREFIX}${feature}`);

        return SplitService.LOCAL_OVERRIDE_TREATMENTS.includes(value || '')
            ? value as SplitIO.Treatment
            : null;
    }

    private isLocalOverrideAllowed(): boolean {
        if (environment.production || typeof window === 'undefined') {
            return false;
        }

        const hostname = window.location.hostname;

        return hostname === 'localhost'
            || hostname === '127.0.0.1'
            || hostname === '::1'
            || hostname.endsWith('.localhost');
    }

    updateKey(key: string): Promise<void> {
        // Every screen that reads a flag calls this on init, always with the same
        // account key. Rebuilding the SDK for a key it already serves re-fetches the
        // split definitions and throws away the impression de-duplication cache,
        // so each rebuild re-sends impressions Split would otherwise have suppressed.
        // Already serving this key, or still starting up on it. Several screens call
        // this concurrently before the SDK reports ready, so checking ready$ alone
        // still let those concurrent calls tear down a client that was mid-flight.
        if (key === this.key && this.initialization) {
            return this.initialization;
        }

        if (!this.client) {
            return this.initialize(key);
        }

        // Claim the key synchronously. initializeSplitClient() only assigns it once
        // the teardown below has resolved, so without this a second caller arriving
        // in the same tick still sees the previous key and rebuilds a second time.
        this.key = key;

        this.initialization = this.client.destroy()
            .catch(() => undefined)
            .then(() => {
                this.treatmentCache.clear();
                return this.initialize(key);
            });

        return this.initialization;
    }

    private useFallbackTreatments(): void {
        this.splitUnavailable = true;
        this.ready$.next(false);
        this.treatmentCache.forEach((subject) => {
            subject.next(SplitService.DEFAULT_TREATMENT);
        });
    }
}
